import { ApiError } from "../api.js";
import { resetPassword } from "../auth.js";
import { esc } from "../utils.js";
import { navigate } from "../app.js";

const MIN_LENGTH = 8; // mirrors the API's check in auth/routes.py

/** Landing page for the link in the reset email (#/reset/<token>).
 * Unprotected: whoever is resetting a password is by definition not
 * signed in, and the signed token is the credential. */
export async function render(container, params) {
  const token = params?.token;

  if (!token) {
    failed(container, "That reset link is incomplete.");
    return;
  }

  container.innerHTML = `
    <div class="mx-auto" style="max-width:26rem;">
      <h1 class="display-heading h3 mb-2">Choose a new password</h1>
      <p class="text-slate">At least ${MIN_LENGTH} characters.</p>
      <form id="reset-form" novalidate>
        <div class="mb-3">
          <label class="form-label" for="reset-pw">New password</label>
          <input type="password" class="form-control" id="reset-pw" autocomplete="new-password" required />
        </div>
        <div class="mb-3">
          <label class="form-label" for="reset-pw2">Confirm new password</label>
          <input type="password" class="form-control" id="reset-pw2" autocomplete="new-password" required />
        </div>
        <div id="reset-error" class="text-danger small mb-3 d-none"></div>
        <button type="submit" class="btn btn-primary w-100" id="reset-submit">Set new password</button>
      </form>
    </div>`;

  const errorEl = container.querySelector("#reset-error");
  const button = container.querySelector("#reset-submit");
  const show = (message) => {
    errorEl.textContent = message;
    errorEl.classList.remove("d-none");
  };

  container.querySelector("#reset-form").addEventListener("submit", async (e) => {
    e.preventDefault();
    errorEl.classList.add("d-none");
    const pw = container.querySelector("#reset-pw").value;
    const pw2 = container.querySelector("#reset-pw2").value;

    // Checked here so an obvious mistake doesn't spend the token — it is
    // single-use, and a rejected attempt still burns the round trip.
    if (pw.length < MIN_LENGTH) return show(`Use at least ${MIN_LENGTH} characters.`);
    if (pw !== pw2) return show("Those passwords don't match.");

    button.disabled = true;
    button.textContent = "Saving...";
    try {
      await resetPassword(token, pw);
      container.innerHTML = `
        <div class="dc-empty text-center py-5 mx-auto" style="max-width:26rem;">
          <i class="bi bi-check-circle text-success" style="font-size:2.5rem;"></i>
          <h1 class="h4 mt-3">Password updated</h1>
          <p class="text-slate">Sign in with your new password.</p>
          <button class="btn btn-primary mt-2" id="reset-continue">Go to sign in</button>
        </div>`;
      container.querySelector("#reset-continue").addEventListener("click", () => navigate("/login"));
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not reset your password.";
      // An expired or spent token can't be recovered from on this screen,
      // so send them back to request a fresh one rather than leaving them
      // retyping a password that will never be accepted.
      const spent = err instanceof ApiError && err.status === 400 &&
        /expired|already been used|not valid|no longer exists/i.test(message);
      if (spent) failed(container, message);
      else {
        show(message);
        button.disabled = false;
        button.textContent = "Set new password";
      }
    }
  });
}

function failed(container, message) {
  container.innerHTML = `
    <div class="dc-empty text-center py-5 mx-auto" style="max-width:26rem;">
      <i class="bi bi-exclamation-triangle text-warning" style="font-size:2.5rem;"></i>
      <h1 class="h4 mt-3">Couldn't reset your password</h1>
      <p class="text-slate">${esc(message)}</p>
      <button class="btn btn-primary mt-2" id="reset-again">Request a new link</button>
    </div>`;
  container.querySelector("#reset-again").addEventListener("click", () => navigate("/forgot-password"));
}
